import {
  GeneralNetworkError,
  PaymentRequiredError,
  UnauthorizedError,
} from '@blocksuite/blocks';
import { type AuthAtom, authAtom } from '@pique/core/atoms';
import {
  addTauriAuthHeaders,
  addTauriAuthToUrlSynchronously,
} from '@pique/core/modules/cloud/utils/tauri-auth';
import {
  cleanupCopilotSessionMutation,
  createCopilotMessageMutation,
  createCopilotSessionMutation,
  forkCopilotSessionMutation,
  getBaseUrl,
  getCopilotHistoriesQuery,
  getCopilotHistoryIdsQuery,
  getCopilotSessionsQuery,
  gqlFetcherFactory,
  type GraphQLQuery,
  type QueryOptions,
  type RequestOptions,
  UserFriendlyError,
} from '@pique/graphql';
import { getCurrentStore } from '@toeverything/infra';
import { type Socket, Manager } from 'socket.io-client';

export interface TranscriptionChunk {
  text: string;
  timestamp: number;
  isFinal: boolean;
  confidence?: number;
  speaker?: number;
  words?: Array<{
    word: string;
    start: number;
    end: number;
    confidence?: number;
  }>;
}

export type TranscriptionProvider = 'deepgram' | 'google-speech';

export interface TranscriptionOptions {
  model?: string;
  language?: string;
  smartFormat?: boolean;
  punctuate?: boolean;
  diarize?: boolean;
  interimResults?: boolean;
  provider?: TranscriptionProvider;
}

export interface TranscriptionStreamController {
  start: (options?: TranscriptionOptions) => Promise<boolean>;
  sendAudio: (data: ArrayBuffer | Blob) => void;
  stop: () => void;
  onResult: (callback: (chunk: TranscriptionChunk) => void) => void;
  onError: (callback: (error: Error) => void) => void;
  onClose: (callback: () => void) => void;
  disconnect: () => void;
  isConnected: () => boolean;
}

// Create fetch wrapper with Tauri auth headers
const fetchWithAuth = async (
  input: string,
  init?: RequestInit
): Promise<Response> => {
  const headers = await addTauriAuthHeaders(init?.headers || {});
  return fetch(input, { ...init, headers });
};

// Create authenticated fetcher
const authenticatedFetcher = gqlFetcherFactory(
  getBaseUrl() + '/graphql',
  fetchWithAuth
);

type OptionsField<T extends GraphQLQuery> =
  RequestOptions<T>['variables'] extends { options: infer U } ? U : never;

function codeToError(error: UserFriendlyError) {
  switch (error.status) {
    case 401:
      return new UnauthorizedError();
    case 402:
      return new PaymentRequiredError();
    default:
      return new GeneralNetworkError(
        error.code
          ? `${error.code}: ${error.message}\nIdentify: ${error.name}`
          : undefined
      );
  }
}

export function resolveError(err: unknown) {
  const standardError = UserFriendlyError.fromAnyError(err);
  return codeToError(standardError);
}

export function handleError(src: unknown) {
  console.error('AI action error:', src);
  const err = resolveError(src);
  if (err instanceof UnauthorizedError) {
    const currentAuth = getCurrentStore().get(authAtom);
    (getCurrentStore().set as (atom: typeof authAtom, value: AuthAtom) => void)(
      authAtom,
      { ...currentAuth, openModal: true }
    );
  }
  console.error('Resolved error:', err);
  return err;
}

const fetcher = async <Query extends GraphQLQuery>(
  options: QueryOptions<Query>
) => {
  try {
    return await authenticatedFetcher<Query>(options);
  } catch (err) {
    throw handleError(err);
  }
};

export class CopilotClient {
  readonly backendUrl = getBaseUrl();

  async createSession(
    options: OptionsField<typeof createCopilotSessionMutation>
  ) {
    const res = await fetcher({
      query: createCopilotSessionMutation,
      variables: {
        options,
      },
    });
    return res.createCopilotSession;
  }

  async forkSession(options: OptionsField<typeof forkCopilotSessionMutation>) {
    const res = await fetcher({
      query: forkCopilotSessionMutation,
      variables: {
        options,
      },
    });
    return res.forkCopilotSession;
  }

  async createMessage(
    options: OptionsField<typeof createCopilotMessageMutation>
  ) {
    // noise reduction: remove verbose console logging of prompts

    const res = await fetcher({
      query: createCopilotMessageMutation,
      variables: {
        options,
      },
    });
    return res.createCopilotMessage;
  }

  async getSessions(workspaceId: string) {
    const res = await fetcher({
      query: getCopilotSessionsQuery,
      variables: {
        workspaceId,
      },
    });
    return res.currentUser?.copilot;
  }

  async getHistories(
    workspaceId: string,
    docId?: string,
    options?: RequestOptions<
      typeof getCopilotHistoriesQuery
    >['variables']['options']
  ) {
    const res = await fetcher({
      query: getCopilotHistoriesQuery,
      variables: {
        workspaceId,
        docId,
        options,
      },
    });

    return res.currentUser?.copilot?.histories;
  }

  async getHistoryIds(
    workspaceId: string,
    docId?: string,
    options?: RequestOptions<
      typeof getCopilotHistoriesQuery
    >['variables']['options']
  ) {
    const res = await fetcher({
      query: getCopilotHistoryIdsQuery,
      variables: {
        workspaceId,
        docId,
        options,
      },
    });

    return res.currentUser?.copilot?.histories;
  }

  async cleanupSessions(input: {
    workspaceId: string;
    docId: string;
    sessionIds: string[];
  }) {
    const res = await fetcher({
      query: cleanupCopilotSessionMutation,
      variables: {
        input,
      },
    });
    return res.cleanupCopilotSession;
  }

  async chatText({
    sessionId,
    messageId,
    signal,
  }: {
    sessionId: string;
    messageId?: string;
    signal?: AbortSignal;
  }) {
    const url = new URL(`${this.backendUrl}/api/copilot/chat/${sessionId}`);
    if (messageId) {
      url.searchParams.set('messageId', messageId);
    }

    // Add Tauri auth headers for compatibility
    const headers = await addTauriAuthHeaders();
    const response = await fetch(url.toString(), { signal, headers });
    return response.text();
  }

  // Text or image to text
  chatTextStream(
    {
      sessionId,
      messageId,
    }: {
      sessionId: string;
      messageId?: string;
    },
    endpoint = 'stream'
  ) {
    const url = new URL(
      `${this.backendUrl}/api/copilot/chat/${sessionId}/${endpoint}`
    );
    if (messageId) url.searchParams.set('messageId', messageId);
    const updatedUrl = addTauriAuthToUrlSynchronously(url.toString());
    return new EventSource(updatedUrl);
  }

  // Text or image to images
  imagesStream(
    sessionId: string,
    messageId?: string,
    seed?: string,
    endpoint = 'images'
  ) {
    const url = new URL(
      `${this.backendUrl}/api/copilot/chat/${sessionId}/${endpoint}`
    );
    if (messageId) {
      url.searchParams.set('messageId', messageId);
    }
    if (seed) {
      url.searchParams.set('seed', seed);
    }
    const updatedUrl = addTauriAuthToUrlSynchronously(url.toString());
    return new EventSource(updatedUrl);
  }

  createTranscriptionStream(): TranscriptionStreamController {
    let socket: Socket | null = null;
    let resultCallback: ((chunk: TranscriptionChunk) => void) | null = null;
    let errorCallback: ((error: Error) => void) | null = null;
    let closeCallback: (() => void) | null = null;
    let connected = false;

    const connect = (): Socket => {
      if (socket?.connected) {
        return socket;
      }

      const wsUrl = addTauriAuthToUrlSynchronously(`${this.backendUrl}/`);
      const manager = new Manager(wsUrl, {
        autoConnect: false,
        transports: ['websocket'],
        secure: location.protocol === 'https:',
      });

      socket = manager.socket('/');

      socket.on('connect', () => {
        connected = true;
      });

      socket.on('disconnect', () => {
        connected = false;
        closeCallback?.();
      });

      socket.on('connect_error', (err: Error) => {
        connected = false;
        if (err.message === 'unauthenticated' || err.message.includes('401')) {
          const currentAuth = getCurrentStore().get(authAtom);
          (getCurrentStore().set as (atom: typeof authAtom, value: AuthAtom) => void)(
            authAtom,
            { ...currentAuth, openModal: true }
          );
          errorCallback?.(new UnauthorizedError());
        } else {
          errorCallback?.(err);
        }
      });

      socket.on('transcribe:result', (chunk: TranscriptionChunk) => {
        resultCallback?.(chunk);
      });

      socket.on('transcribe:error', (data: { message: string }) => {
        if (data.message.includes('unauthenticated') || data.message.includes('401')) {
          const currentAuth = getCurrentStore().get(authAtom);
          (getCurrentStore().set as (atom: typeof authAtom, value: AuthAtom) => void)(
            authAtom,
            { ...currentAuth, openModal: true }
          );
          errorCallback?.(new UnauthorizedError());
        } else {
          errorCallback?.(new Error(data.message));
        }
      });

      socket.on('transcribe:closed', () => {
        closeCallback?.();
      });

      socket.connect();
      return socket;
    };

    const controller: TranscriptionStreamController = {
      start: async (options?: TranscriptionOptions): Promise<boolean> => {
        const sock = connect();

        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            resolve(false);
          }, 10000);

          sock.emit(
            'transcribe:start',
            {
              options: options || {},
              provider: options?.provider,
            },
            (response: { data: { success: boolean; provider?: string } }) => {
              clearTimeout(timeout);
              resolve(response?.data?.success ?? false);
            }
          );
        });
      },

      sendAudio: (data: ArrayBuffer | Blob): void => {
        if (!socket?.connected) {
          return;
        }

        const processAndSend = (buffer: ArrayBuffer) => {
          const base64 = btoa(
            String.fromCharCode(...new Uint8Array(buffer))
          );
          socket?.emit('transcribe:audio', { data: base64 });
        };

        if (data instanceof Blob) {
          data.arrayBuffer().then(processAndSend);
        } else {
          processAndSend(data);
        }
      },

      stop: (): void => {
        if (!socket?.connected) {
          return;
        }
        socket.emit('transcribe:stop', {});
      },

      onResult: (callback: (chunk: TranscriptionChunk) => void): void => {
        resultCallback = callback;
      },

      onError: (callback: (error: Error) => void): void => {
        errorCallback = callback;
      },

      onClose: (callback: () => void): void => {
        closeCallback = callback;
      },

      disconnect: (): void => {
        if (socket) {
          socket.disconnect();
          socket = null;
        }
        connected = false;
      },

      isConnected: (): boolean => {
        return connected && (socket?.connected ?? false);
      },
    };

    return controller;
  }
}
